import { useState } from "react";
import { toast } from "sonner";

import Money from "@/components/money";
import { Button } from "@buildpy/ui/button";
import { Checkbox } from "@buildpy/ui/checkbox";
import { DialogFooter } from "@buildpy/ui/dialog";
import { savePriceCandidates } from "@/api/actions/quote";
import { settleRedirect } from "@/lib/redirect-error";
import type { VatMode } from "@buildpy/core/modules/money/vat";
import { marginPctText } from "@buildpy/core/modules/quote/editor/sheet/grid-model";
import { convertUnitPrice } from "@buildpy/core/modules/quote/margin";
import type { PriceCandidate } from "@buildpy/core/modules/quote/price-candidates";
import { vatBasisLabel } from "@buildpy/core/modules/quote/snapshot-vat";

interface Props {
  quoteId: string;
  candidates: readonly PriceCandidate[];
  // 이 견적의 단가 방식 — 판매가를 편집기와 같은 기준으로 보여 준다(저장은 공급가 그대로).
  vatMode: VatMode;
  // 저장·저장 안 함이 끝나면 부른다 — 발송 대화상자의 닫기와 같은 길.
  onDone: () => void;
}

const signedPct = (bp: number): string => `${bp >= 0 ? "+" : ""}${marginPctText(bp / 10_000)}%`;

// 판매가 안내 — 별도 견적은 전부 공급가, 포함 견적은 판매만 포함 금액이고 원가·단가표는 공급가.
const basisNoteOf = (mode: VatMode): string =>
  mode === "EXCL"
    ? `금액은 ${vatBasisLabel("EXCL")}(공급가)`
    : `판매 금액은 이 견적과 같은 ${vatBasisLabel(mode)} · 원가는 공급가 · 단가표에는 공급가로 저장됩니다`;

// 발송 뒤 단가표에 없는 새 품목 — 고른 줄만 넣는다(후보 금액은 공급가 · 판매는 견적 방식으로 바꿔 보여 준다).
const PriceCandidatesSection = ({ quoteId, candidates, vatMode, onDone }: Props) => {
  const [picked, setPicked] = useState<ReadonlySet<string>>(
    () => new Set(candidates.filter((c) => c.default_checked).map((c) => c.key))
  );
  const [pending, setPending] = useState(false);
  if (candidates.length === 0) return null;

  const toggle = (key: string, on: boolean) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });

  const save = async () => {
    setPending(true);
    try {
      const result = await savePriceCandidates(quoteId, Array.from(picked));
      if (!result.ok) {
        toast.error(result.message);
        return;
      }
      toast.success(result.saved > 0 ? `단가표에 ${result.saved}개를 저장했습니다` : "새로 저장할 품목이 없습니다");
      onDone();
    } catch (error) {
      if (settleRedirect(error)) return;
      toast.error("새 품목을 저장하지 못했습니다");
    } finally {
      setPending(false);
    }
  };

  return (
    <section aria-labelledby="price-candidates-title" className="flex flex-col gap-y-2 border-t border-border pt-3" data-testid="price-candidates">
      <h3 className="text-sm font-semibold" id="price-candidates-title">
        새 품목 {candidates.length}개를 저장할까요
      </h3>
      <p className="text-xs text-muted-foreground">
        원가와 마진이 저장되고, 판매가를 직접 고친 줄은 그 마진으로 저장됩니다 · {basisNoteOf(vatMode)}
      </p>
      <ul className="flex max-h-64 flex-col overflow-y-auto rounded-md border border-border">
        {candidates.map((c) => {
          const sale = convertUnitPrice(c.material_unit_price, "EXCL", vatMode) + convertUnitPrice(c.labor_unit_price, "EXCL", vatMode);
          return (
            <li key={c.key} className="border-b border-border last:border-0">
              <label className="flex cursor-pointer items-start gap-x-2 px-3 py-2 hover:bg-muted/50">
                <Checkbox
                  aria-label={`${c.item} 저장`}
                  checked={picked.has(c.key)}
                  className="mt-1"
                  data-testid="price-candidate-check"
                  disabled={pending}
                  onCheckedChange={(state) => toggle(c.key, state === true)}
                />
                <span className="flex min-w-0 flex-col">
                  <span className="break-words text-sm">{c.item}</span>
                  <span className="text-xs text-muted-foreground">
                    {[c.trade_name, c.scope, c.unit].filter((part) => part.trim() !== "").join(" · ")}
                  </span>
                  <span className="flex flex-wrap items-center gap-x-1 text-xs text-muted-foreground">
                    {c.cost_unit_price !== null && c.effective_margin_bp !== null ? (
                      <>
                        원가 <Money value={c.cost_unit_price} /> · {signedPct(c.effective_margin_bp)} → 판매 <Money className="text-foreground" value={sale} />
                      </>
                    ) : (
                      <>
                        원가 비움 · 판매 <Money className="text-foreground" value={sale} />
                      </>
                    )}
                  </span>
                </span>
              </label>
            </li>
          );
        })}
      </ul>
      <DialogFooter>
        <Button disabled={pending} size="sm" variant="ghost" onClick={onDone}>
          저장 안 함
        </Button>
        <Button disabled={pending || picked.size === 0} size="sm" variant="primary" onClick={() => void save()}>
          {picked.size}개 저장
        </Button>
      </DialogFooter>
    </section>
  );
};

export default PriceCandidatesSection;
